import {useCallback, useEffect, useRef} from 'react'
import useEffectEvent from '../../hooks/use-effect-event'

/**
 * Hook that wraps a callback to be executed on the next animation frame.
 * Ensures the callback is only executed if the component is still mounted,
 * preventing state updates on unmounted components.
 *
 * Features:
 * - Automatic cleanup: cancels all pending animation frames on unmount
 * - React 18 Strict Mode compatible
 * - Queueing behavior: multiple calls schedule multiple animation frames
 * - Always uses latest callback implementation without causing re-renders
 *
 * @param {Function} fn - Callback function to be executed on the next animation frame.
 *                        Can accept arguments when the trigger function is called.
 * @returns {Function} Trigger function that schedules the callback on the next animation frame.
 *                     Accepts any arguments which will be forwarded to the callback.
 *
 * @example
 * // Event handler optimization with scroll events
 * const MyComponent = () => {
 *   const handleScroll = useAnimationFrameCallback(() => {
 *     console.log('Scroll handled on animation frame')
 *   })
 *
 *   useEffect(() => {
 *     window.addEventListener('scroll', handleScroll)
 *     return () => window.removeEventListener('scroll', handleScroll)
 *   }, [handleScroll])
 *
 *   return <div>Content</div>
 * }
 *
 * @example
 * // Throttling rapid state updates with argument forwarding
 * const MyComponent = () => {
 *   const [value, setValue] = useState(0)
 *
 *   const updateValue = useAnimationFrameCallback((newValue) => {
 *     setValue(newValue)
 *   })
 *
 *   const handleChange = (e) => {
 *     // Argument is forwarded to the callback
 *     updateValue(e.target.value)
 *   }
 *
 *   return <input onChange={handleChange} />
 * }
 */
const useAnimationFrameCallback = (fn) => {
    // Track if the component is mounted to prevent state updates after unmount
    const mountedRef = useRef(true)

    // Store pending animation frame IDs to cancel them on unmount
    const rafIdsRef = useRef(new Set())

    useEffect(() => {
        mountedRef.current = true
        const rafIds = rafIdsRef.current
        return () => {
            mountedRef.current = false
            // Cancel all pending animation frames on unmount to prevent memory leaks
            rafIds.forEach((id) => cancelAnimationFrame(id))
            rafIds.clear()
        }
    }, [])

    // Create stable callback reference that always uses latest fn
    const callback = useEffectEvent(fn)

    const trigger = useCallback(
        (...args) => {
            const rafId = requestAnimationFrame(() => {
                // Remove this RAF ID from the set once it executes
                rafIdsRef.current.delete(rafId)

                // Only execute callback if component is still mounted
                if (!mountedRef.current) return

                callback(...args)
            })

            // Track this RAF ID so we can cancel it if needed
            rafIdsRef.current.add(rafId)
        },
        [callback]
    )

    return trigger
}

export default useAnimationFrameCallback
